import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Image as ImageIcon, Tag, AlertCircle, Check, ArrowLeft, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import TextEditor from './TextEditor';
import SEOPreview from './SEOPreview';

interface Article {
  id?: string;
  title: string;
  content: string;
  main_keyword: string;
  image_url: string;
  description: string;
  category: string;
  created_at?: string;
  updated_at?: string;
  meta_description?: string;
  og_image_url?: string;
  slug?: string;
}

interface ArticleEditorProps {
  onClose?: () => void;
}

const DEFAULT_IMAGE_URL = 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&w=800&q=80';

const ArticleEditor: React.FC<ArticleEditorProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [user, setUser] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'content' | 'seo'>('content');
  const [metaDescription, setMetaDescription] = useState('');
  const [ogImageUrl, setOgImageUrl] = useState('');
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [article, setArticle] = useState<Article>({
    title: '',
    content: '',
    main_keyword: '',
    image_url: DEFAULT_IMAGE_URL,
    description: '',
    category: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      onClose ? onClose() : navigate('/');
      return;
    }

    if (slug) {
      loadArticle(slug);
    }
  }, [slug, user]);

  const loadArticle = async (articleSlug: string) => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('slug', articleSlug)
        .single();

      if (error) throw error;
      if (data) {
        setArticle({
          ...data,
          image_url: data.image_url || DEFAULT_IMAGE_URL
        });
        setPreviewUrl(data.image_url || DEFAULT_IMAGE_URL);
        setMetaDescription(data.meta_description || '');
        setOgImageUrl(data.og_image_url || '');
      }
    } catch (error) {
      console.error('Error loading article:', error);
      setError('Erreur lors du chargement de l\'article');
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('L\'image ne doit pas dépasser 5MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Format d\'image non supporté. Utilisez JPG, PNG ou WebP');
      return;
    }

    setImageFile(file);
    setError(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const sanitizeFileName = (name: string): string => {
    // Remove accents and special characters
    const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Replace any non-alphanumeric characters (except hyphens) with hyphens
    return normalized.toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-') // Replace multiple consecutive hyphens with a single one
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  const uploadImage = async (): Promise<string> => {
    if (!imageFile) {
      return article.image_url || DEFAULT_IMAGE_URL;
    }

    setIsUploadingImage(true);
    try {
      if (article.image_url && article.image_url !== DEFAULT_IMAGE_URL) {
        const oldImagePath = article.image_url.split('/').pop();
        if (oldImagePath) {
          await supabase.storage
            .from('article-images')
            .remove([`articles/${oldImagePath}`]);
        }
      }

      const fileExt = imageFile.name.split('.').pop();
      const sanitizedKeyword = sanitizeFileName(article.main_keyword);
      const fileName = `${Date.now()}-${sanitizedKeyword}.${fileExt}`;
      const filePath = `articles/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('article-images')
        .upload(filePath, imageFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('article-images')
        .getPublicUrl(uploadData.path);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Erreur lors de l\'upload de l\'image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const convertUrlsToLinks = (content: string): string => {
    const urlRegex = /(https?:\/\/[^\s<]+)/g;
    return content.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      let image_url = article.image_url;
      if (imageFile) {
        image_url = await uploadImage();
      }

      const processedContent = convertUrlsToLinks(article.content);

      const articleData = {
        ...article,
        content: processedContent,
        image_url,
        meta_description: metaDescription || article.description,
        og_image_url: ogImageUrl || image_url,
        updated_at: new Date().toISOString()
      };

      if (!articleData.id) {
        articleData.id = crypto.randomUUID();
        articleData.created_at = new Date().toISOString();
      }

      const { error: saveError } = await supabase
        .from('articles')
        .upsert(articleData, {
          onConflict: ['id']
        })
        .select('id');

      if (saveError) throw saveError;

      setSuccess('Article sauvegardé avec succès !');
      setTimeout(() => {
        onClose ? onClose() : navigate('/resources');
      }, 2000);
    } catch (error: any) {
      console.error('Error saving article:', error);
      setError('Erreur lors de la sauvegarde de l\'article');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return <div className="text-center py-8 text-gray-500">Chargement de l'éditeur...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        {slug ? 'Modifier l\'article' : 'Nouvel article'}
      </h1>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 text-green-600 p-4 rounded-md mb-6 flex items-center">
          <Check className="w-5 h-5 mr-2" />
          <p>{success}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('content')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'content'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Contenu
            </button>
            <button
              onClick={() => setActiveTab('seo')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'seo'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              SEO
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {activeTab === 'content' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titre *
                </label>
                <input
                  type="text"
                  value={article.title}
                  onChange={(e) => setArticle({ ...article, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={article.description}
                  onChange={(e) => setArticle({ ...article, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                <select
                  value={article.category}
                  onChange={(e) => setArticle({ ...article, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Sélectionner une catégorie</option>
                  <option value="Recrutement">Recrutement</option>
                  <option value="Conseils">Conseils</option>
                  <option value="Technologie">Technologie</option>
                  <option value="Tendances">Tendances</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot-clé principal (SEO) *</label>
                <input
                  type="text"
                  value={article.main_keyword}
                  onChange={(e) => setArticle({ ...article, main_keyword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image mise en avant *</label>
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt="Article preview"
                    className="w-full max-w-md h-48 object-cover rounded-lg mb-4"
                  />
                )}
                <input
                  ref={imageInputRef}
                  type="file"
                  onChange={handleImageChange}
                  accept="image/*"
                  className="w-full"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Format recommandé : 1200x630px, max 5MB
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contenu *</label>
                <TextEditor
                  value={article.content}
                  onChange={(content) => setArticle({ ...article, content })}
                  height="500px"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meta Description
                </label>
                <textarea
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  rows={3}
                  placeholder="Description qui apparaîtra dans les résultats de recherche"
                />
                <p className="mt-1 text-sm text-gray-500">
                  {metaDescription.length}/160 caractères recommandés
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image OpenGraph
                </label>
                <input
                  type="text"
                  value={ogImageUrl}
                  onChange={(e) => setOgImageUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="URL de l'image pour les réseaux sociaux"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Format recommandé : 1200x630px
                </p>
              </div>

              <div className="mt-8">
                <SEOPreview
                  url={`https://pimpmyjob.fr/resources/${article.slug || ''}`}
                  title={article.title}
                  description={metaDescription || article.description}
                  imageUrl={ogImageUrl || article.image_url}
                />
              </div>
            </>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => onClose ? onClose() : navigate('/resources')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Publier</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArticleEditor;